import { ElMessage } from 'element-plus'
import { upload, uploadGI } from '@/api/common.js'

// ============================================
// 分类映射表 - 前端标识符 → 后端 OSS 分类
// 基于后端 common/tool/oss_path.go 的 categoryPool 定义
// ============================================
const CATEGORY_MAP = {
  // ========================================
  // 系统基础分类 (后端常量定义)
  // ========================================
  'game': 'game',               // 游戏相关
  'banner': 'banner',           // 横幅
  'activity': 'activity',       // 活动
  'media': 'media',             // 媒体文件
  'site': 'site',               // 站点资源
  'platform': 'platform',       // 平台相关

  // ========================================
  // API 接口分类 (后端 categoryPool 直接支持)
  // ========================================
  'box': 'treasure_box',        // 宝箱图片 → treasure_box
  'item': 'props',              // 道具/饰品图片 → props
  'rooms': 'rooms',             // 房间图片
  'headimg': 'avatar',          // 用户头像 → avatar
  'music': 'music',             // 音乐文件
  'column': 'column',           // 栏目图片
  'excel': 'excel',             // Excel 文件
  'popup': 'popup',             // 弹窗图片
  'vip': 'vip',                 // VIP 相关图片
  'misc': 'misc',               // 未分类文件

  // ========================================
  // 前端语义化别名 → 后端分类
  // ========================================

  // 横幅相关变体
  'banner1': 'banner',
  'banner2': 'banner',
  'banner3': 'banner',
  'lobby_banner': 'banner',
  'home_banner': 'banner',

  // 栏目/分类相关变体
  'lobby_module': 'column',
  'lobby_icon': 'column',
  'category': 'column',         // 常见错误:前端使用 'category' → 应该是 'column'
  'game_category': 'column',
  'module': 'column',

  // 道具/物品相关变体
  'prop': 'props',              // item → props
  'props': 'props',             // item → props
  'item_icon': 'props',         // item → props
  'item_image': 'props',        // item → props
  'treasure': 'treasure_box',   // box → treasure_box
  'treasure_box': 'treasure_box',

  // 头像相关变体
  'avatar': 'avatar',           // headimg → avatar
  'user_avatar': 'avatar',      // headimg → avatar
  'profile': 'avatar',          // headimg → avatar
  'avatar_border': 'avatar',    // headimg → avatar

  // 弹窗相关变体
  'dialog': 'popup',
  'modal': 'popup',
  'popup_bg': 'popup',

  // 游戏相关变体
  'game_icon': 'game',
  'game_cover': 'game',
  'manufacturer': 'game',
  'merchant': 'game',

  // 平台相关变体
  'payment_channel': 'platform',
  'recharge': 'platform',

  // 活动相关变体
  'activity_icon': 'activity',
  'activity_image': 'activity',
  'event': 'activity',

  // 房间相关变体
  'room': 'rooms',

  // 音乐相关变体
  'audio': 'music',

  // VIP 相关变体
  'vip_icon': 'vip',

  // 默认分类
  'default': 'site'
}

/**
 * 将前端分类标识符映射到后端 OSS 分类
 * @param {string} frontendCategory - 前端使用的分类标识符
 * @returns {string} 后端 OSS 分类名称
 */
export const mapCategoryToOss = (frontendCategory) => {
  const key = (frontendCategory || 'default').toLowerCase().trim()
  const ossCategory = CATEGORY_MAP[key]

  if (!ossCategory) {
    console.warn(`⚠️ 未找到分类映射: "${frontendCategory}", 使用默认分类 "site"`)
    return 'site'
  }

  if (process.env.NODE_ENV === 'development') {
    console.log(`✅ 分类映射: ${frontendCategory} → ${ossCategory}`)
  }

  return ossCategory
}

/**
 * 验证图片文件类型和大小
 * @param {File} file - 要验证的文件
 * @param {number} maxSize - 最大文件大小，默认5MB
 * @param {string[]} allowedTypes - 允许的文件类型，默认为常见图片格式
 * @returns {boolean} 验证是否通过
 */
export const validateImageFile = (file, maxSize = 5 * 1024 * 1024, allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']) => {
  // 验证文件类型
  if (!allowedTypes.includes(file.type)) {
    ElMessage.error('只能上传 JPG、PNG、GIF、WebP 格式的图片!')
    return false
  }

  // 验证文件大小
  if (file.size > maxSize) {
    ElMessage.error(`图片大小不能超过 ${Math.round(maxSize / 1024 / 1024)}MB!`)
    return false
  }

  return true
}

/**
 * 创建图片上传处理函数
 * @param {Function} onSuccess - 上传成功回调函数,接收上传结果
 * @param {Function} onError - 上传失败回调函数,可选
 * @param {string} category - 上传分类,默认为'site'
 * @returns {Function} 返回用于 el-upload 的 http-request 函数
 */
export const createImageUploadHandler = (onSuccess, onError, category = 'site') => {
  return async (options) => {
    // 验证文件
    if (!validateImageFile(options.file)) {
      return
    }

    // 映射前端分类到后端 OSS 分类
    const ossCategory = mapCategoryToOss(category)

    const formData = new FormData()
    formData.append('category', ossCategory)
    formData.append('file', options.file)

    try {
      const res = await upload(formData)
      if (res && res.data && res.data.key) {
        onSuccess(res.data.key)
      } else {
        throw new Error('上传返回数据格式错误')
      }
    } catch (error) {
      console.error('上传失败:', error)
      ElMessage.error('上传失败，请重试')
      if (onError) {
        onError(error)
      }
    }
  }
}

export const createGameImageUploadHandler = (onSuccess, onError, gameCode, merchantId, language, typo, category = 'game') => {
  return async (options) => {
    // 验证文件
    if (!validateImageFile(options.file)) {
      return
    }

    const formData = new FormData()
    formData.append('category', "game")
    formData.append('file', options.file)
    formData.append("game_code", gameCode)
    formData.append("merchant_id", merchantId)
    formData.append("language", language)
    formData.append("typo", typo)

    try {
      const res = await uploadGI(formData)
      if (res && res.data && res.data.key) {
        onSuccess(res.data.key)
      } else {
        throw new Error('上传返回数据格式错误')
      }
    } catch (error) {
      console.error('上传失败:', error)
      ElMessage.error('上传失败，请重试')
      if (onError) {
        onError(error)
      }
    }
  }
}

/**
 * 获取图片上传的 accept 属性值
 * @returns {string} accept 属性值
 */
export const getImageAcceptTypes = () => {
  return 'image/jpeg,image/jpg,image/png,image/gif,image/webp'
}

/**
 * 获取图片上传的提示文字
 * @param {number} maxSize - 最大文件大小，默认5MB
 * @returns {string} 提示文字
 */
export const getImageUploadTip = (maxSize = 5) => {
  return `支持 JPG、PNG、GIF、WebP 格式，大小不超过 ${maxSize}MB`
} 